import * as React from 'react';
import { observer } from 'mobx-react';
import { getDiagnosticsLogState } from '../store/store';
import {
    DetailsList,
    ConstrainMode,
    Selection,
    SelectionMode,
    DetailsListLayoutMode,
    IColumn,
} from 'office-ui-fabric-react/lib/components/DetailsList';
import { DayOfWeek } from 'office-ui-fabric-react/lib/utilities/dateValues/DateValues';
import { selectFindMeetingTimesDiagnosticsItem } from '../actions/findMeetingTimesRequestDiagnostics';
import {
    FindMeetingTimesRequestDataPoint,
    DiagnosticsLogger,
} from '../store/schema/DiagnosticsLogState';
import { Inspector, chromeLight } from 'react-inspector';

const styles = require('./DiagnosticsPanel.scss');

const LOGGER_NAME = 'Cas';

const inspectorTheme = {
    ...chromeLight,
    TREENODE_FONT_FAMILY: 'Menlo, Consolas, monospace',
    TREENODE_FONT_SIZE: '12px',
    OBJECT_NAME_COLOR: 'rgb(180, 0, 158)',
    OBJECT_VALUE_NULL_COLOR: 'rgb(102, 102, 102)',
    OBJECT_VALUE_UNDEFINED_COLOR: 'rgb(102, 102, 102)',
    OBJECT_VALUE_REGEXP_COLOR: 'rgb(232, 17, 35)',
    OBJECT_VALUE_STRING_COLOR: 'rgb(232, 17, 35)',
    OBJECT_VALUE_SYMBOL_COLOR: 'rgb(232, 17, 35)',
    OBJECT_VALUE_NUMBER_COLOR: 'rgb(0, 120, 215)',
    OBJECT_VALUE_BOOLEAN_COLOR: 'rgb(0, 120, 215)',
    OBJECT_VALUE_FUNCTION_KEYWORD_COLOR: 'rgb(216, 59, 1)',
};

@observer
export default class FindMeetingTimesDiagnostics extends React.Component<{}, {}> {
    private columns: IColumn[] = [
        {
            name: 'Request ID',
            fieldName: 'requestId',
            key: 'requestId',
            minWidth: 250,
        },

        {
            name: 'Status',
            fieldName: 'status',
            key: 'status',
            minWidth: 100,
        },

        {
            name: 'Request Time',
            fieldName: 'requestTime',
            key: 'requestTime',
            minWidth: 200,
        },

        {
            name: 'Server',
            fieldName: 'server',
            key: 'server',
            minWidth: 100,
        },
    ];

    private debugColumns: IColumn[] = [
        {
            name: 'Day',
            fieldName: 'day',
            key: 'day',
            minWidth: 200,
        },

        {
            name: 'Start meeting time',
            fieldName: 'start',
            key: 'start',
            minWidth: 200,
        },

        {
            name: 'End meeting time',
            fieldName: 'end',
            key: 'end',
            minWidth: 200,
        },
    ];

    private getKey = item => item.requestId;

    private selection = new Selection({
        onSelectionChanged: () => {
            const selectionCount = this.selection.getSelectedCount();
            const key =
                selectionCount > 0
                    ? (this.selection.getSelection()[0] as FindMeetingTimesRequestDataPoint)
                          .requestId
                    : null;
            selectFindMeetingTimesDiagnosticsItem(key);
        },
        canSelectItem: () => true,
        selectionMode: SelectionMode.single,
        getKey: this.getKey,
    });

    private renderItemColumn = (item, index, column) => {
        return <span>{item[column.key]}</span>;
    };

    render() {
        let logger: DiagnosticsLogger<
            FindMeetingTimesRequestDataPoint
        > = getDiagnosticsLogState().loggers.get(LOGGER_NAME);
        let datapoints: any = logger.datapoints;
        let selected = logger.selected;
        let selectedInfo: any = {};
        let items: any = {};

        if (datapoints) {
            items = datapoints.map(datapoint => {
                let server = '';

                if (datapoint.server) {
                    server = datapoint.server.split('.')[0];
                }

                return {
                    requestId: datapoint.requestId,
                    status: datapoint.status,
                    requestTime: datapoint.requestTime,
                    server: server,
                };
            });
        }

        if (selected && selected.debug) {
            let debugObject = JSON.parse(selected.debug);
            let debugKey: any = {};
            let debugValue: any = {};
            let meetingHours: any = {};
            let times: any = {};
            let server = '';

            if (debugObject && debugObject.length > 0) {
                debugKey = debugObject[0].Key;
                debugValue = debugObject[0].Value;
            }

            if (debugValue) {
                meetingHours = JSON.parse(debugValue).MeetingHours;
            }

            if (meetingHours) {
                times = meetingHours.map(elem => {
                    if (!elem) {
                        return {};
                    }
                    const workDay: boolean = elem.t[0] && elem.t[0].s != elem.t[0].e;
                    const notWorkDayMsg: string = 'Not a workday';

                    return {
                        day: dayOfWeek(elem.wd),
                        start: workDay ? getMeetingTimeFromMinutes(elem.t[0].s) : notWorkDayMsg,
                        end: workDay ? getMeetingTimeFromMinutes(elem.t[0].e) : notWorkDayMsg,
                    };
                });
            }

            if (selected.server) {
                server = selected.server.split('.')[0];
            }

            selectedInfo = {
                requestId: selected.requestId,
                status: selected.status,
                requestTime: selected.requestTime,
                server: server,
                debugKey: debugKey,
                debugValue: times,
            };
        }

        return (
            <div className={styles.container}>
                <div className={styles.table}>
                    <DetailsList
                        items={items}
                        columns={this.columns}
                        compact={true}
                        constrainMode={ConstrainMode.unconstrained}
                        setKey="Cas"
                        layoutMode={DetailsListLayoutMode.fixedColumns}
                        selection={this.selection}
                        selectionMode={SelectionMode.single}
                        selectionPreservedOnEmptyClick={true}
                        onRenderItemColumn={this.renderItemColumn}
                    />
                </div>
                {selected && (
                    <div className={styles.details}>
                        <div>
                            <h3>Summary</h3>
                            <Inspector data={selectedInfo} theme={inspectorTheme} />
                            <DetailsList
                                items={selectedInfo.debugValue}
                                columns={this.debugColumns}
                                compact={true}
                                constrainMode={ConstrainMode.unconstrained}
                                setKey="CasSelected"
                                layoutMode={DetailsListLayoutMode.fixedColumns}
                                selectionMode={SelectionMode.none}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export function renderFindMeetingTimesDiagnostics() {
    return <FindMeetingTimesDiagnostics />;
}

function dayOfWeek(day: DayOfWeek): string {
    if (day == null || day < 0 || day > 6) {
        return '';
    }
    for (let key of Object.keys(DayOfWeek)) {
        if (DayOfWeek[key] === day) {
            return DayOfWeek[key];
        }
    }
    return null;
}

function getMeetingTimeFromMinutes(minutes: number) {
    if (!minutes) {
        minutes = 0;
    }

    let millisec = new Date().setUTCHours(0, minutes, 0, 0);
    return new Date(millisec).toLocaleTimeString();
}
